import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/** Stato del collegamento al DB di frontiera Elan (gestionale Business Cube). */
export interface FrontieraStatus {
  stato: "ok" | "attenzione" | "errore" | "disattivato";
  abilitata: boolean;
  server: string;
  database: string;
  utente: string;
  viste_attese: number;
  verificato_il: string;
  da_cache?: boolean;
  porta?: number;
  porta_da?: "sql_browser" | "configurazione" | "predefinita";
  latenza_ms?: number;
  versione?: string;
  livello?: string;
  edizione?: string;
  permessi?: { db_owner: boolean; db_datareader: boolean; db_datawriter: boolean };
  viste_visibili?: number;
  viste_mancanti?: string[];
  viste_inattese?: string[];
  /** Ora locale del server (Italia), senza fuso. */
  viste_modificate_il?: string | null;
  avvisi?: string[];
  errore?: { tipo: string; messaggio: string; referente: string };
}

const CHIAVE = ["admin-frontiera"];

/** Query separata da quella di Sistema: un gestionale lento non ferma la pagina. */
export function useAdminFrontiera() {
  return useQuery({
    queryKey: CHIAVE,
    queryFn: () => apiFetch<FrontieraStatus>("/api/v1/admin/frontiera"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** Rifà il controllo adesso, saltando la cache di un minuto del server. */
export function useRicontrollaFrontiera() {
  const qc = useQueryClient();
  return async () => {
    const fresco = await apiFetch<FrontieraStatus>("/api/v1/admin/frontiera?forza=true");
    qc.setQueryData(CHIAVE, fresco);
  };
}

/** Ordini e anagrafiche letti dal gestionale: stato per fonte (F2, 05-10-2026). */
export interface SyncFonte {
  fonte: "ordini" | "anagrafica_articoli" | "anagrafica_clienti" | "m3";
  /** Solo per «m3» (NDP e DDT): da che data la lettura è accesa; null = spenta. */
  attivo_dal?: string | null;
  esito: "mai" | "invariato" | "aggiornato" | "errore";
  ultimo_controllo_il: string | null;
  ultimo_cambio_il: string | null;
  righe: number | null;
  errore: { tipo: string; messaggio: string; referente: string } | null;
}

const CHIAVE_SYNC = ["admin-frontiera-sincronizzazione"];

export function useSincronizzazioneGestionale() {
  return useQuery({
    queryKey: CHIAVE_SYNC,
    queryFn: () => apiFetch<SyncFonte[]>("/api/v1/admin/frontiera/sincronizzazione"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** Un giro adesso, senza aspettare Cloud Scheduler. */
export function useSincronizzaOra() {
  const qc = useQueryClient();
  return async () => {
    const esito = await apiFetch<{ esito: string }>("/api/v1/admin/frontiera/sincronizza", {
      method: "POST",
    });
    await qc.invalidateQueries({ queryKey: CHIAVE_SYNC });
    await qc.invalidateQueries({ queryKey: ["dashboard", "stato-dati"] });
    return esito;
  };
}
